import { Component, inject, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { firstValueFrom } from "rxjs";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { SyncService } from "@bitwarden/common/abstractions/sync/sync.service.abstraction";
import { Utils } from "@bitwarden/common/misc/utils";
import { RouterService } from "@bitwarden/web-vault/app/core";
import { ConfirmationDialogComponent } from "@bitwarden/web-vault/app/components/confirmation-dialog/confirmation-dialog.component";
import {
  TeamInvitationResponse,
  TeamInvitationService,
  TeamInvitationType,
} from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-invitation.service";
import {
  classifyInviteFailure,
  inviteErrorMessage,
} from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-invitation-errors";
import { TEAM_STATUS_BADGE_CLASS } from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-status";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";

// Errors retrying can't fix: the link itself is bad, the invite is gone, the backend rejected
// the decline for good (see classifyInviteFailure), or the page couldn't work out who's
// signed in. Only these replace the invite card; anything else (a dropped connection, a 5xx)
// is shown above it with Accept / Decline / Skip still there to retry.
const FATAL_ERROR_KEYS = new Set([
  "invalidInviteLink",
  "inviteNoLongerPending",
  "inviteLinkSuperseded",
  "inviteLinkExpired",
  // A team invite's decline uses the same token as its accept, so a permanent decline failure
  // means accepting can't work either (an org invite's doesn't use the token: see
  // ORG_INVITE_RECOVERABLE_KEYS). A
  // permanent accept failure isn't fatal: some (2FA required, single-org policy, revoked access,
  // deleted team) leave Decline working, so only Accept is hidden (acceptBlocked).
  "inviteDeclineFailedPermanent",
  "unexpectedError",
]);

// Of those, the ones an org invite survives with one button left: its decline goes by
// organizationUserId, not the link's token (gloss-back#98), so a stale or expired link only
// stops Accept, and a decline rejected for good says nothing about Accept.
const ORG_INVITE_RECOVERABLE_KEYS = new Set([
  "inviteLinkSuperseded",
  "inviteLinkExpired",
  "inviteDeclineFailedPermanent",
]);

const TEAMS_ROUTE = ["/manage", GlossRoutes.Collections];

// Landing page for both team invite emails, so the invitee gets the same page whether or not
// they're already in the inviter's org. The link tells the two apart:
// - organizationUserId: first team in an org they haven't joined (send_org_invite). Accepting
//   still waits for an admin to confirm the membership.
// - collectionId: one more team in an org they already belong to (send_collection_invite).
//   Accepting grants access straight away.
// TeamInvitationService picks the matching accept/decline endpoint for each.
@Component({
  selector: "app-accept-team",
  templateUrl: "accept-team.component.html",
})
export class AcceptTeamComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private teamInvitationService = inject(TeamInvitationService);
  private syncService = inject(SyncService);
  private stateService = inject(StateService);
  private i18nService = inject(I18nService);
  private platformUtilsService = inject(PlatformUtilsService);
  private logService = inject(LogService);
  private routerService = inject(RouterService);
  private dialog = inject(MatDialog);

  readonly badgeClass = TEAM_STATUS_BADGE_CLASS;
  // Dark-teal primary action (color-action-primary), same family as the app's other primary
  // buttons, rather than gloss-button's default bright "primary".
  readonly primaryButtonClass =
    "tw-h-11 tw-w-full tw-rounded-2xl tw-bg-color-action-primary tw-px-6 tw-text-sm tw-font-semibold tw-text-color-text-on-action tw-shadow-elevation-card";

  type: TeamInvitationType | null = null;
  organizationId = "";
  organizationUserId = "";
  collectionId = "";
  token = "";

  // Display-only: taken from the link as-is, the backend never trusts them.
  teamName = "";
  avatarInitial = "";
  // Who sent the invite and how many people are already in the team, as the email link
  // carries them. Display-only: the link is the only source (there's no unauthenticated
  // endpoint to look them up), so they're optional with generic fallbacks.
  inviterDisplay = "";
  memberCount: number | null = null;
  // Who the email was sent to (both email templates put the recipient in `email`).
  invitedEmail = "";
  currentUserEmail = "";

  loading = false;
  error: string | null = null;
  // True when `error` is one retrying can't fix (see FATAL_ERROR_KEYS): the template then shows
  // only the error. Otherwise the error sits above the card and the buttons stay.
  fatal = false;
  // The backend rejected the accept for good (see classifyInviteFailure): Accept is hidden for
  // this visit, Decline / Skip stay, and the error shows above the card.
  acceptBlocked = false;
  // Why Accept is hidden, kept inline in its place: a later error (e.g. a retryable decline
  // failure) replaces the alert, which would otherwise leave no Accept and no reason (#6260).
  acceptBlockedReason: string | null = null;
  // An org invite's decline was rejected for good: Decline is hidden for this visit, Accept stays
  // (it goes by the link's token, which the decline doesn't use).
  declineBlocked = false;
  isAuthenticated = false;
  done = false;
  // Where to go once accepted/declined, and the message shown with the Continue button that
  // takes the user there if the automatic navigation doesn't.
  doneRoute: string[] = ["/"];
  doneMessageKey = "";

  // The backend rejects a token whose email isn't the signed-in user's, so say so up front
  // instead of letting Accept fail with a generic error.
  get isDifferentAccount(): boolean {
    return (
      !!this.invitedEmail &&
      !!this.currentUserEmail &&
      this.invitedEmail.toLowerCase() !== this.currentUserEmail.toLowerCase()
    );
  }

  async ngOnInit() {
    const params = this.route.snapshot.queryParams;
    this.organizationId = params["organizationId"];
    this.organizationUserId = params["organizationUserId"] || "";
    this.collectionId = params["collectionId"] || "";
    this.token = params["token"];
    this.type = this.collectionId ? "Collection" : this.organizationUserId ? "Organization" : null;
    // collectionName: team invite emails. teamName: org invite emails. organizationName: org
    // invite emails sent before the rename (redirected from accept-organization).
    this.teamName =
      params["collectionName"] ||
      params["teamName"] ||
      params["organizationName"] ||
      this.i18nService.t("team");
    this.avatarInitial = this.teamName.charAt(0).toUpperCase();
    this.invitedEmail = params["email"] || "";
    this.inviterDisplay = params["inviter"] || this.i18nService.t("someone");
    const memberCount = parseInt(params["memberCount"], 10);
    this.memberCount = Number.isFinite(memberCount) && memberCount > 0 ? memberCount : null;

    try {
      const secondId = this.type === "Collection" ? this.collectionId : this.organizationUserId;
      if (
        !this.type ||
        !Utils.isGuid(this.organizationId) ||
        !Utils.isGuid(secondId) ||
        !this.token
      ) {
        this.logService.warning("Invalid team invite link params");
        this.setError("invalidInviteLink");
        return;
      }

      this.isAuthenticated = await this.stateService.getIsAuthenticated();
      if (this.isAuthenticated) {
        this.currentUserEmail = (await this.stateService.getEmail()) || "";
      }
    } catch (e) {
      this.logAndSetError(e, "unexpectedError");
    }
  }

  async accept() {
    if (this.loading || this.done || this.acceptBlocked) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      await this.teamInvitationService.accept(this.toInvitation());
    } catch (e) {
      const failure = classifyInviteFailure(e, "accept");
      this.logAndSetError(e, failure.key);
      // A stale or expired link that isn't fatal (an org invite's, see setError) only stops Accept.
      this.acceptBlocked =
        failure.kind === "permanent" || (failure.kind === "superseded" && !this.fatal);
      this.acceptBlockedReason = this.acceptBlocked ? this.error : null;
      // Only cleared on failure: on success it stays on through the sync and navigation (leave()
      // clears it), so the done state's Continue only shows if the user is still here afterwards.
      this.loading = false;
      return;
    }

    this.done = true;
    this.doneMessageKey = "inviteAccepted";
    if (this.type === "Organization") {
      // Nothing to show yet: the team only becomes visible once an admin confirms the member.
      this.platformUtilsService.showToast(
        "success",
        this.i18nService.t("inviteAccepted"),
        this.i18nService.t("inviteAcceptedJoinedOwner", this.teamName),
      );
      await this.leave(["/"]);
      return;
    }

    this.platformUtilsService.showToast("success", "", this.i18nService.t("inviteAccepted"));
    // Pull the new collection (and its key) in before landing on the Teams page, otherwise the
    // team the user just joined wouldn't be listed yet.
    await this.syncService.fullSync(true).catch((e) => this.logError(e));
    await this.leave(TEAMS_ROUTE);
  }

  declineInvitation() {
    if (this.loading || this.done || this.declineBlocked) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: "confirmation-dialog",
      data: {
        title: "declineThisInvitation",
        type: "warning",
        // The dialog renders its message as HTML and the name comes from the link: escape it.
        message: this.i18nService.t(
          "declineInvitationConfirmation",
          Utils.escapeHtml(this.teamName),
        ),
        customizedButton: {
          yes: { text: "decline", class: "primary" },
          no: { text: "keepInvitation", class: "neutral" },
        },
      },
    });

    void firstValueFrom(dialogRef.afterClosed()).then(async (confirmed) => {
      if (!confirmed || this.loading) {
        return;
      }

      this.loading = true;
      this.error = null;

      let succeeded = false;
      try {
        // An org invite's decline only deletes the signed-in account's membership while it's
        // still Invited (gloss-back#98): someone else's link, or an invite accepted since, comes
        // back "Invitation not found" rather than removing a member from the org.
        await this.teamInvitationService.decline(this.toInvitation());
        succeeded = true;
      } catch (e) {
        const failure = classifyInviteFailure(e, "decline");
        this.logAndSetError(e, failure.key);
        this.declineBlocked = this.type === "Organization" && failure.kind === "permanent";
      } finally {
        // As in accept(): on success leave() clears it once navigation settles.
        if (!succeeded) {
          this.loading = false;
        }
      }

      if (succeeded) {
        this.done = true;
        this.doneMessageKey = "invitationDeclined";
        this.platformUtilsService.showToast(
          "success",
          "",
          this.i18nService.t("invitationDeclined"),
        );
        await this.leave(["/"]);
      }
    });
  }

  // Shown once accepted/declined, in case leaving the page didn't happen on its own.
  continueAfterDone() {
    if (this.loading) {
      return;
    }
    void this.leave(this.doneRoute);
  }

  // "Invited by inviter.A@gloss.com · 1 member" under the team name.
  get cardSubtitle(): string {
    const count =
      this.memberCount == null
        ? ""
        : this.memberCount === 1
          ? this.i18nService.t("oneMember")
          : this.i18nService.t("membersCount", String(this.memberCount));
    return [this.i18nService.t("invitedByEmail", this.inviterDisplay), count]
      .filter(Boolean)
      .join(" · ");
  }

  // Leaves the invite pending; it stays in Teams > Invitations.
  skipForNow() {
    void this.router.navigate(["/"]);
  }

  navigateToLogin() {
    this.routerService.setPreviousUrl(this.router.url);
    void this.router.navigate(["/login"]);
  }

  private toInvitation(): TeamInvitationResponse {
    return new TeamInvitationResponse({
      Type: this.type,
      OrganizationId: this.organizationId,
      OrganizationUserId: this.organizationUserId || null,
      CollectionId: this.collectionId || null,
      CollectionName: this.type === "Collection" ? this.teamName : null,
      Token: this.token,
    });
  }

  // Navigates away after accept/decline. A failed navigation (rejected, or resolved false when a
  // guard cancels it) only gets logged. If it fails before leaving, the page stays on its done
  // state, whose Continue button retries. For Teams, once the /views step succeeds the page is
  // gone, so a failed Teams step (e.g. OrganizationsGuard turning away a non-beta user) leaves the
  // user on /views, the intended fallback. loading stays on until navigation settles, so Continue
  // can't start a second one.
  private async leave(route: string[]) {
    this.doneRoute = route;
    this.loading = true;
    try {
      // The user layout's InitialRedirectGuard sends any first entry other than /views to
      // /views, so going straight from here to Teams always ends up on /views. Enter the layout
      // through /views first: the guard doesn't run again for navigations within it.
      if (route === TEAMS_ROUTE && !(await this.navigate(["/" + GlossRoutes.Views]))) {
        return;
      }
      await this.navigate(route);
    } catch (e) {
      this.logError(e);
    } finally {
      this.loading = false;
    }
  }

  private async navigate(route: string[]): Promise<boolean> {
    const navigated = await this.router.navigate(route);
    if (navigated === false) {
      this.logService.warning(`Navigation to ${route.join("/")} was cancelled`);
    }
    return navigated !== false;
  }

  private logError(e: unknown) {
    this.logService.error(e instanceof Error ? String(e) : inviteErrorMessage(e));
  }

  private logAndSetError(e: unknown, key: string) {
    this.logError(e);
    this.setError(key);
  }

  private setError(key: string) {
    this.error = this.i18nService.t(key);
    this.fatal =
      FATAL_ERROR_KEYS.has(key) &&
      !(this.type === "Organization" && ORG_INVITE_RECOVERABLE_KEYS.has(key));
  }
}
